import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { User, Store, Eye, EyeOff, Mail, CheckCircle, ArrowLeft, Loader2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CreatorApplicationModal from './CreatorApplicationModal';
import { Turnstile } from 'react-turnstile';

function AuthModal({ isOpen, onClose, onSuccess, defaultTab = 'login', prefillEmail = '' }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [loginData, setLoginData] = useState({ email: prefillEmail || '', password: '' });
  
  // Get referral code from URL or localStorage
  const getReferralCode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const refFromUrl = urlParams.get('ref');
    if (refFromUrl) {
      localStorage.setItem('incoming_ref', refFromUrl);
      return refFromUrl;
    }
    return localStorage.getItem('incoming_ref') || '';
  };
  
  const [signupData, setSignupData] = useState({ 
    email: prefillEmail || '', 
    password: '', 
    full_name: '',      // Legal name for verification
    store_name: '',     // Brand/Store name (for sellers)
    role: 'buyer',
    termsAccepted: false,  // Terms and conditions acknowledgment
    referral_code: getReferralCode()  // Capture referral code
  });

  // Sync the prefilled email into the form when the prop changes (e.g.
  // user arrives via /?signup=1&email=... deep-link from the email
  // popup CTA). useState(initialValue) only fires on first mount, so
  // without this effect the prop update is silently dropped and the
  // email field stays empty. Tests confirmed this regression on iter 71.
  useEffect(() => {
    if (!prefillEmail) return;
    setLoginData((prev) => ({ ...prev, email: prev.email || prefillEmail }));
    setSignupData((prev) => ({ ...prev, email: prev.email || prefillEmail }));
  }, [prefillEmail]);
  
  // 2FA State
  const [show2FAInput, setShow2FAInput] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [tempToken, setTempToken] = useState('');
  
  // Turnstile State
  const [loginTurnstileToken, setLoginTurnstileToken] = useState('');
  const [signupTurnstileToken, setSignupTurnstileToken] = useState('');
  const loginTurnstileRef = useRef(null);
  const signupTurnstileRef = useRef(null);
  // Creator Application Modal State
  const [showCreatorApplication, setShowCreatorApplication] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post('/auth/login', {
        ...loginData,
        email: (loginData.email || '').trim().toLowerCase(),
        turnstile_token: loginTurnstileToken
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setTempToken(response.data.temp_token);
        setShow2FAInput(true);
        setLoading(false);
        return;
      }
      
      // Check if email is verified
      if (!response.data.user.email_verified) {
        toast.warning('Please verify your email to access all features');
      }
      
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.session_token);
      toast.success('Welcome back!');
      onSuccess(response.data.user);
      onClose();
      // Creators / admins land in /dashboard so they can pick up their
      // setup progress (Stripe Connect, products, etc.).
      const role = response.data.user?.role;
      if ((role === 'creator' || role === 'admin') && typeof window !== 'undefined') {
        window.location.assign('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (twoFACode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post('/auth/login/verify-2fa', {
        temp_token: tempToken,
        code: twoFACode
      });
      
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.session_token);
      toast.success('Welcome back!');
      onSuccess(response.data.user);
      onClose();
      const role2fa = response.data.user?.role;
      if ((role2fa === 'creator' || role2fa === 'admin') && typeof window !== 'undefined') {
        window.location.assign('/dashboard');
      }

      // Reset 2FA state
      setShow2FAInput(false);
      setTwoFACode('');
      setTempToken('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post('/auth/forgot-password', { email: (forgotPasswordEmail || '').trim().toLowerCase() });
      setResetEmailSent(true);
      toast.success('Password reset link sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        email: (signupData.email || '').trim().toLowerCase(),
        password: signupData.password,
        name: signupData.full_name,
        role: signupData.role,
        referral_code: signupData.referral_code || null,
        // Sub-Affiliate auto-bind: when a visitor lands via a Founding
        // Creator's recruiter link `?af=<code>`, we stash the code in
        // localStorage under `incoming_af` on first page load (see
        // App.js). Forward it on signup so the backend can attach the
        // new user to the recruiter's downstream graph. Independent
        // from the regular 1-tier `referral_code` chain — a user can
        // simultaneously have both.
        af_code: (typeof window !== 'undefined' ? localStorage.getItem('incoming_af') : null) || null,
        turnstile_token: signupTurnstileToken
      };

      const response = await axios.post('/auth/register', payload);

      // Clear the stored referral code after successful registration
      localStorage.removeItem('incoming_ref');
      // Clear the sub-affiliate recruiter code too — it should only
      // attach once, at the moment of signup, never again.
      localStorage.removeItem('incoming_af');

      // (signup analytics counter is now bumped server-side in /auth/register
      //  so we don't need to fire a client-side tracker here.)

      // Show verification message
      setVerificationSent(true);

      // Still log them in but show verification reminder
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.session_token);

      // Creators land in /dashboard immediately after signup so they can
      // finish onboarding (Stripe Connect, first product, etc.). Buyers
      // stay on whatever page they signed up from.
      const isCreator = signupData.role === 'creator';

      setTimeout(() => {
        toast.success('Account created! Please check your email to verify your account.');
        onSuccess(response.data.user);
        onClose();
        if (isCreator && typeof window !== 'undefined') {
          window.location.assign('/dashboard');
        }
      }, 2000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Verification sent success screen
  if (verificationSent) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('auth.checkYourEmail')}</h2>
            <p className="text-neutral-400 mb-4">
              {t('auth.verificationSentTo')}<br />
              <span className="text-white font-semibold">{signupData.email}</span>
            </p>
            <div className="bg-neutral-800 rounded-xl p-4 text-sm text-neutral-300 mb-6">
              <p className="mb-2">📧 {t('auth.clickLinkInEmail')}</p>
              <p>✅ {t('auth.canStartExploring')}</p>
            </div>
            
            {/* Affiliate Promotion for Buyers */}
            {signupData.role === 'buyer' && (
              <div className="bg-gradient-to-r from-[#00FF94]/10 to-[#00F0FF]/10 border border-[#00FF94]/30 rounded-xl p-4 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00FF94] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">💰</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-[#00FF94] mb-1">Become an Affiliate & Earn!</h4>
                    <p className="text-xs text-neutral-400 mb-3">
                      Love AI products? Share them and earn commissions on every sale you refer!
                    </p>
                    <button
                      onClick={() => {
                        onClose();
                        window.location.href = '/affiliate/program';
                      }}
                      className="text-sm font-semibold text-[#00F0FF] hover:text-[#00D4DD] flex items-center gap-1 transition-colors"
                    >
                      Learn More →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center mb-4">
            {t('auth.welcomeTo')} theailedger.ai
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-neutral-800">
            <TabsTrigger value="login" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              {t('auth.signIn')}
            </TabsTrigger>
            <TabsTrigger value="signup" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              {t('auth.signUp')}
            </TabsTrigger>
          </TabsList>

          {/* LOGIN TAB */}
          <TabsContent value="login" className="mt-6">
            {show2FAInput ? (
              /* 2FA Verification Form */
              <form onSubmit={handle2FAVerify} className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-[#00F0FF]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Two-Factor Authentication</h3>
                  <p className="text-neutral-400 text-sm">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
                
                <div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="bg-neutral-800 border-white/10 text-center text-3xl tracking-[0.5em] font-mono h-14"
                    autoFocus
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || twoFACode.length !== 6}
                  className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Sign In'
                  )}
                </Button>
                
                <button
                  type="button"
                  onClick={() => {
                    setShow2FAInput(false);
                    setTwoFACode('');
                    setTempToken('');
                  }}
                  className="w-full text-sm text-neutral-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to login
                </button>
              </form>
            ) : !showForgotPassword ? (
              <>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">{t('auth.email')}</label>
                    <Input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="you@example.com"
                      className="bg-neutral-800 border-white/10"
                      required
                      data-testid="login-email"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold">{t('auth.password')}</label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setForgotPasswordEmail(loginData.email);
                        }}
                        className="text-xs text-[#00F0FF] hover:text-[#00C2CC] transition-colors"
                      >
                        {t('auth.forgotPassword')}
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        type={showLoginPassword ? "text" : "password"}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="••••••••"
                        className="bg-neutral-800 border-white/10 pr-10"
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {process.env.REACT_APP_TURNSTILE_SITE_KEY && (
                    <div className="flex justify-center">
                      <Turnstile
                        ref={loginTurnstileRef}
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={(token) => setLoginTurnstileToken(token)}
                        onExpire={() => setLoginTurnstileToken('')}
                        theme="dark"
                        size="flexible"
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-full"
                    data-testid="login-submit"
                  >
                    {loading ? t('auth.signingIn') : t('auth.signIn')}
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-neutral-900 text-neutral-400">{t('auth.orContinueWith')}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full border-white/10 hover:bg-white/5 rounded-full"
                  data-testid="google-login"
                >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </Button>
              </>
            ) : (
              /* FORGOT PASSWORD VIEW */
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmailSent(false);
                  }}
                  className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>

                {!resetEmailSent ? (
                  <>
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                        <Mail className="w-8 h-8 text-[#00F0FF]" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Forgot Password?</h3>
                      <p className="text-neutral-400 text-sm">
                        Enter your email and we&apos;ll send you a link to reset your password.
                      </p>
                    </div>

                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Email Address</label>
                        <Input
                          type="email"
                          value={forgotPasswordEmail}
                          onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="bg-neutral-800 border-white/10"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-full"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Reset Link'
                        )}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Check Your Email</h3>
                    <p className="text-neutral-400 text-sm mb-4">
                      We&apos;ve sent a password reset link to:
                    </p>
                    <p className="text-[#00F0FF] font-semibold mb-6">{forgotPasswordEmail}</p>
                    <p className="text-neutral-500 text-xs">
                      Didn&apos;t receive the email? Check your spam folder or{' '}
                      <button
                        type="button"
                        onClick={() => setResetEmailSent(false)}
                        className="text-[#00F0FF] hover:underline"
                      >
                        try again
                      </button>
                    </p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* SIGNUP TAB */}
          <TabsContent value="signup" className="mt-6">
            <form onSubmit={handleSignup} className="space-y-4">
              {/* Full Legal Name */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Full Legal Name <span className="text-neutral-500 font-normal">(for verification)</span>
                </label>
                <Input
                  type="text"
                  value={signupData.full_name}
                  onChange={(e) => setSignupData({ ...signupData, full_name: e.target.value })}
                  placeholder="John Michael Doe"
                  className="bg-neutral-800 border-white/10"
                  required
                  data-testid="signup-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold mb-2">Email</label>
                <Input
                  type="email"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="bg-neutral-800 border-white/10"
                  required
                  data-testid="signup-email"
                />
              </div>

              {/* Password with visibility toggle */}
              <div>
                <label className="block text-sm font-semibold mb-2">Password</label>
                <div className="relative">
                  <Input
                    type={showSignupPassword ? "text" : "password"}
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    placeholder="••••••••"
                    className="bg-neutral-800 border-white/10 pr-10"
                    required
                    minLength={6}
                    data-testid="signup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    {showSignupPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-1">Minimum 6 characters</p>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-semibold mb-2">I want to...</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSignupData({ ...signupData, role: 'buyer', store_name: '' })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      signupData.role === 'buyer'
                        ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    data-testid="role-buyer"
                  >
                    <User className={`w-6 h-6 mx-auto mb-2 ${signupData.role === 'buyer' ? 'text-[#00F0FF]' : 'text-neutral-400'}`} />
                    <p className="font-semibold text-sm">Buy Products</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      // Sellers MUST go through the VIP application
                      // (/submit) — direct self-signup as creator was
                      // closed off after a non-approved user slipped
                      // through as a Seller in admin/users. Send them
                      // to the application form and dismiss the modal.
                      try { onClose && onClose(); } catch (_) {}
                      if (typeof window !== 'undefined') {
                        window.location.assign('/submit');
                      }
                    }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      signupData.role === 'creator'
                        ? 'border-white bg-white text-black/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    data-testid="role-creator"
                  >
                    <Store className={`w-6 h-6 mx-auto mb-2 ${signupData.role === 'creator' ? 'text-white' : 'text-neutral-400'}`} />
                    <p className="font-semibold text-sm">Apply to Sell</p>
                  </button>
                </div>
              </div>

              {/* Store/Brand Name - Only for Sellers */}
              {signupData.role === 'creator' && (
                <div className="animate-in slide-in-from-top-2 duration-200">
                  <div className="bg-gradient-to-r from-white/10 to-[#00F0FF]/10 border border-white/30 rounded-xl p-4">
                    <h4 className="font-semibold text-sm text-white mb-2 flex items-center gap-2">
                      <Store className="w-4 h-4" />
                      Creator Application Required
                    </h4>
                    <p className="text-xs text-neutral-400 mb-3">
                      To maintain quality on our marketplace, creator accounts require approval. 
                      Click the button below to fill out a quick application.
                    </p>
                    <ul className="text-xs text-neutral-400 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-[#00FF94]" />
                        Review within 24-48 hours
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-[#00FF94]" />
                        Access to Creator Dashboard & Analytics
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-[#00FF94]" />
                        Exclusive Creator Lounge community
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Terms - Only show for buyers */}
              {signupData.role === 'buyer' && (
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={signupData.termsAccepted}
                    onChange={(e) => setSignupData({ ...signupData, termsAccepted: e.target.checked })}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 cursor-pointer"
                    required
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms-checkbox" className="text-sm text-neutral-400 cursor-pointer">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/terms', '_blank')}
                      className="text-[#00F0FF] hover:underline"
                    >
                      Terms & Conditions
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/terms#refund-policy', '_blank')}
                      className="text-[#00F0FF] hover:underline"
                    >
                      Refund Policy
                    </button>
                  </label>
                </div>
              )}

              {process.env.REACT_APP_TURNSTILE_SITE_KEY && (
                <div className="flex justify-center">
                  <Turnstile
                    ref={signupTurnstileRef}
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setSignupTurnstileToken(token)}
                    onExpire={() => setSignupTurnstileToken('')}
                    theme="dark"
                    size="flexible"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || (signupData.role === 'buyer' && !signupData.termsAccepted)}
                className={`w-full font-bold rounded-full disabled:opacity-50 disabled:cursor-not-allowed ${
                  signupData.role === 'creator'
                    ? 'bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black'
                    : 'bg-[#00F0FF] hover:bg-[#00C2CC] text-black'
                }`}
                data-testid="signup-submit"
              >
                {loading ? 'Please wait...' : signupData.role === 'creator' ? 'Apply to Become a Creator' : 'Create Account'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-neutral-900 text-neutral-400">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full border-white/10 hover:bg-white/5 rounded-full"
              data-testid="google-signup"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
      
      {/* Creator Application Modal */}
      <CreatorApplicationModal
        isOpen={showCreatorApplication}
        onClose={() => {
          setShowCreatorApplication(false);
          // Reset signup data when closing
          setSignupData({ ...signupData, role: 'buyer' });
        }}
        prefillData={{
          name: signupData.full_name,
          email: signupData.email,
          password: signupData.password,
          store_name: signupData.store_name
        }}
      />
    </Dialog>
  );
}

export default AuthModal;
